import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useFeeScheduleActions,
  useFeeServices,
  useFeeSettings,
} from '@/features/billing/api';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import type { FeeService } from '@/features/billing/types';
import { useBranch } from '@/features/branch/branch-context';
import { useLabCatalogue, useUpdateLabTest } from '@/features/orders/api';
import type { CatalogueTest } from '@/features/orders/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import styles from '@/pages/queue/queue.module.css';
import { DeliverySettingsCard } from './DeliverySettingsCard';
import { HomeVisitSettingsCard } from './HomeVisitSettingsCard';
import { LabPackagesCard } from './LabPackagesCard';
import { VideoFeeCard } from './VideoFeeCard';

const rm = (sen: number | null) => (sen === null ? '—' : formatRM(sen));
const optSen = (s: string) => (s.trim() ? parseRM(s) : null);

/**
 * Admin v2 "Services & prices" (artifacts #169–171) and "Service tax and fee
 * limits" (#170): every service with duration, price, member price and
 * branches; the video consult fee (RL-1900); test prices; blood test
 * packages and home-visit settings (RL-1138); service tax switched on only
 * once registered.
 */
export function ServicesPage() {
  const { hasPermission } = useSession();
  const can = hasPermission('services:manage');
  const toast = useToast();
  const { data, isLoading, error, refetch } = useFeeServices(true);
  const [editing, setEditing] = useState<FeeService | 'new' | null>(null);
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });

  const columns: Column<FeeService>[] = [
    {
      key: 'name',
      header: 'Service',
      render: (s) => (
        <>
          <p className={styles.name}>{s.name}</p>
          {!s.isActive && <Badge>Deleted</Badge>}
        </>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (s) => (s.durationMinutes ? `${s.durationMinutes} min` : '—'),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (s) => (
        <>
          {rm(s.priceSen)}{' '}
          {s.feeWarning && (
            <Badge tone="warning">
              {s.feeWarning === 'above_fee_schedule'
                ? 'Above the fee schedule'
                : 'Below the fee schedule'}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: 'member',
      header: 'Member price',
      align: 'right',
      render: (s) => rm(s.memberPriceSen),
    },
    {
      key: 'branches',
      header: 'Branches',
      render: (s) =>
        s.allBranches ? 'All' : s.branches.map((b) => b.name).join(', '),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (s) =>
        can ? (
          <Button size="sm" onClick={() => setEditing(s)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <Card
        title="Services & prices"
        flush
        actions={
          can && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={14} />}
              onClick={() => setEditing('new')}
            >
              Add service
            </Button>
          )
        }
      >
        <DataTable
          caption="Services and prices"
          columns={columns}
          rows={data}
          rowKey={(s) => s.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={<EmptyState title="No services yet" />}
        />
      </Card>
      <VideoFeeCard can={can} />
      <LabTestPrices can={can} onError={fail} />
      <LabPackagesCard can={can} />
      <HomeVisitSettingsCard can={can} />
      <DeliverySettingsCard can={can} />
      <TaxAndLimits can={can} onError={fail} />
      {editing && (
        <ServiceDialog
          service={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onError={fail}
        />
      )}
    </PageContainer>
  );
}

function ServiceDialog({
  service: s,
  onClose,
  onError,
}: {
  service: FeeService | null;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const { branches } = useBranch();
  const act = useFeeScheduleActions();
  const toast = useToast();
  const [name, setName] = useState(s?.name ?? '');
  const [duration, setDuration] = useState(
    s?.durationMinutes ? String(s.durationMinutes) : '',
  );
  const [price, setPrice] = useState(
    s?.priceSen != null ? senToInput(s.priceSen) : '',
  );
  const [member, setMember] = useState(
    s?.memberPriceSen != null ? senToInput(s.memberPriceSen) : '',
  );
  const [picked, setPicked] = useState<string[]>(
    s && !s.allBranches ? s.branches.map((b) => b.id) : [],
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const priceSen = optSen(price);
  const memberSen = optSen(member);
  const valid =
    name.trim().length >= 2 &&
    (!price.trim() || priceSen !== null) &&
    (!member.trim() || memberSen !== null) &&
    !(priceSen !== null && memberSen !== null && memberSen > priceSen);
  const body = {
    name: name.trim(),
    durationMinutes: duration ? Number(duration) : null,
    priceSen,
    memberPriceSen: memberSen,
    branchIds: picked,
  };
  const done = (title: string) => () => {
    toast.show({ tone: 'success', title });
    onClose();
  };
  return (
    <Dialog
      open
      title={s ? `Edit ${s.name}` : 'Add service'}
      onClose={onClose}
      footer={
        <>
          {s && s.isActive && (
            <Button
              variant="danger"
              loading={act.update.isPending && confirmDelete}
              onClick={() =>
                confirmDelete
                  ? act.update
                      .mutateAsync({ id: s.id, isActive: false })
                      .then(done('Service deleted'))
                      .catch(onError)
                  : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'Tap again to delete' : 'Delete'}
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={act.create.isPending || act.update.isPending}
            onClick={() =>
              (s
                ? act.update.mutateAsync({
                    id: s.id,
                    ...body,
                    ...(s.isActive ? {} : { isActive: true }),
                  })
                : act.create.mutateAsync(body)
              )
                .then(done(s ? 'Price updated everywhere' : 'Service added'))
                .catch(onError)
            }
          >
            {s ? 'Save changes' : 'Add service'}
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <Field label="Name">
          <Input value={name} onChange={(ev) => setName(ev.target.value)} />
        </Field>
        <Field label="Duration (minutes)">
          <Input
            inputMode="numeric"
            value={duration}
            onChange={(ev) => setDuration(ev.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <Field label="Price (RM)">
          <Input
            inputMode="decimal"
            value={price}
            onChange={(ev) => setPrice(ev.target.value)}
          />
        </Field>
        <Field
          label="Member price (RM)"
          error={
            priceSen !== null && memberSen !== null && memberSen > priceSen
              ? 'Higher than the price'
              : undefined
          }
        >
          <Input
            inputMode="decimal"
            value={member}
            onChange={(ev) => setMember(ev.target.value)}
          />
        </Field>
        <ChipToggle
          label="Branches (none chosen = all)"
          options={branches.map((b) => ({ value: b.id, label: b.name }))}
          selected={picked}
          onChange={setPicked}
        />
      </div>
    </Dialog>
  );
}

function LabTestPrices({
  can,
  onError,
}: {
  can: boolean;
  onError: (err: unknown) => void;
}) {
  const { data } = useLabCatalogue(true);
  const update = useUpdateLabTest();
  const columns: Column<CatalogueTest>[] = [
    {
      key: 'name',
      header: 'Test',
      render: (t) => (
        <>
          <p className={styles.name}>{t.name}</p>
          <p className={styles.sub}>{t.tube}</p>
        </>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (t) =>
        can ? (
          <PriceInput
            label={`Price for ${t.name}`}
            sen={t.priceSen}
            onSave={(priceSen) =>
              update.mutateAsync({ id: t.id, priceSen }).catch(onError)
            }
          />
        ) : (
          rm(t.priceSen)
        ),
    },
    {
      key: 'member',
      header: 'Member price',
      align: 'right',
      render: (t) =>
        can ? (
          <PriceInput
            label={`Member price for ${t.name}`}
            sen={t.memberPriceSen}
            onSave={(memberPriceSen) =>
              update.mutateAsync({ id: t.id, memberPriceSen }).catch(onError)
            }
          />
        ) : (
          rm(t.memberPriceSen)
        ),
    },
    {
      key: 'panel',
      header: 'Panels cover',
      render: (t) => (t.panelCovered ? 'Yes' : 'No'),
    },
  ];
  return (
    <Card title="Lab tests" flush>
      <DataTable
        caption="Lab test prices"
        columns={columns}
        rows={data}
        rowKey={(t) => t.id}
        empty={<EmptyState title="No tests in the catalogue" />}
      />
    </Card>
  );
}

function PriceInput({
  label,
  sen,
  onSave,
}: {
  label: string;
  sen: number | null;
  onSave: (sen: number | null) => void;
}) {
  const [v, setV] = useState(sen != null ? senToInput(sen) : '');
  return (
    <Input
      aria-label={label}
      inputMode="decimal"
      value={v}
      onChange={(ev) => setV(ev.target.value)}
      onBlur={() => {
        const next = optSen(v);
        if (v.trim() && next === null)
          return setV(sen != null ? senToInput(sen) : '');
        if (next !== sen) onSave(next);
      }}
    />
  );
}

function TaxAndLimits({
  can,
  onError,
}: {
  can: boolean;
  onError: (err: unknown) => void;
}) {
  const { data } = useFeeSettings();
  if (!data) return null;
  return (
    <TaxForm
      key={JSON.stringify(data)}
      can={can}
      onError={onError}
      settings={data}
    />
  );
}

function TaxForm({
  can,
  onError,
  settings: d,
}: {
  can: boolean;
  onError: (err: unknown) => void;
  settings: NonNullable<ReturnType<typeof useFeeSettings>['data']>;
}) {
  const { t } = useTranslation();
  const act = useFeeScheduleActions();
  const toast = useToast();
  const [on, setOn] = useState(d.serviceTax.enabled);
  const [reg, setReg] = useState(d.serviceTax.sstRegistrationNo ?? '');
  const [max, setMax] = useState(
    d.feeLimits.maxGpFeeSen != null ? senToInput(d.feeLimits.maxGpFeeSen) : '',
  );
  const [min, setMin] = useState(
    d.feeLimits.minGpFeeSen != null ? senToInput(d.feeLimits.minGpFeeSen) : '',
  );
  const [rate, setRate] = useState(
    d.serviceTax.rateBps === null ? '' : String(d.serviceTax.rateBps / 100),
  );
  const [threshold, setThreshold] = useState(
    d.serviceTax.thresholdSen === null
      ? ''
      : senToInput(d.serviceTax.thresholdSen),
  );
  const [confirmed, setConfirmed] = useState(d.serviceTax.scopeConfirmed);
  const pct = Math.min(
    100,
    Math.round(
      (d.serviceTax.thresholdSen
        ? d.serviceTax.last12MonthsSen / d.serviceTax.thresholdSen
        : 0) * 100,
    ),
  );
  return (
    <Card title="Service tax and fee limits">
      <div className={styles.drawer}>
        <ChipToggle
          label="Service tax"
          single
          options={[
            { value: 'off', label: 'Off' },
            { value: 'on', label: 'On' },
          ]}
          selected={[on ? 'on' : 'off']}
          onChange={(v) => can && v[0] && setOn(v[0] === 'on')}
        />
        <Field
          label="SST registration number"
          hint="Switched on only once the clinic is registered."
        >
          <Input
            disabled={!can}
            value={reg}
            onChange={(ev) => setReg(ev.target.value)}
          />
        </Field>
        <Field label={t('tax.rate')}>
          <Input
            disabled={!can}
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </Field>
        <Field label={t('tax.threshold')}>
          <Input
            disabled={!can}
            inputMode="decimal"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </Field>
        <Field label={t('tax.scope')}>
          <label>
            <input
              type="checkbox"
              disabled={!can}
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {t('tax.scopeBody')}
          </label>
        </Field>
        <p>
          Total billed turnover in the last 12 months:{' '}
          {formatRM(d.serviceTax.last12MonthsSen)}. This is not a
          taxable-turnover assessment.
          {d.serviceTax.thresholdSen !== null && (
            <>
              {' '}
              Configured threshold: {formatRM(d.serviceTax.thresholdSen)} ({pct}
              % of threshold).
            </>
          )}
        </p>
        <Field
          label="Highest GP consultation fee (RM)"
          hint="Clinic-entered fee limit. Prices above it are flagged."
        >
          <Input
            disabled={!can}
            inputMode="decimal"
            value={max}
            onChange={(ev) => setMax(ev.target.value)}
          />
        </Field>
        <Field label="Lowest GP consultation fee (RM)">
          <Input
            disabled={!can}
            inputMode="decimal"
            value={min}
            onChange={(ev) => setMin(ev.target.value)}
          />
        </Field>
        {can && (
          <div className={styles.consultFooter}>
            <Button
              variant="primary"
              loading={act.saveSettings.isPending}
              onClick={() =>
                act.saveSettings
                  .mutateAsync({
                    serviceTaxEnabled: on,
                    serviceTaxRateBps: rate.trim()
                      ? Math.round(Number(rate) * 100)
                      : null,
                    serviceTaxThresholdSen: optSen(threshold),
                    serviceTaxScopeConfirmed: confirmed,
                    sstRegistrationNo: reg.trim() || null,
                    maxGpFeeSen: optSen(max),
                    minGpFeeSen: optSen(min),
                  })
                  .then(() => toast.show({ tone: 'success', title: 'Saved' }))
                  .catch(onError)
              }
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
